import axios from 'axios';
import { API } from './constants';
import _ from 'lodash';
import AuthProvider, { needTokenUpdate } from './authProvider';
import Loader from '../components/Loader';
import uuid from 'uuid';
import SessionStorageService from '../services/utilities/SessionStorageService';
import dialogLoader from './dialogLoader';
import { CcusErrorDialog } from '../components/modals/CcusErrorDialog';
import { messageUtility } from './messageUtility';
import { PrintService } from '../services/Prints';

let needLoadingRequestCount = 0;

function showFullScreenLoading() {
  if (needLoadingRequestCount === 0) {
    Loader.start();
  }
  needLoadingRequestCount++;
}

function tryHideFullScreenLoading() {
  if (needLoadingRequestCount <= 0) {
    return;
  }
  needLoadingRequestCount--;
  if (needLoadingRequestCount === 0) {
    setTimeout(tryCloseLoading, 100);
  }
}
const tryCloseLoading = () => {
  if (needLoadingRequestCount === 0) {
    Loader.done();
  }
};

/*
Axios request wrapper by Sheharyar Naseer.
https://sheharyar.me/blog/axios-with-react-for-making-requests/
*/

/**
 * Create an Axios Client with defaults
 */
const client = axios.create({
  baseURL: API.endpoint,
});

// TODO: Move to utility.
/* eslint-disable no-undef */
const isProduction = () => process.env.NODE_ENV === 'production';
/* eslint-enable no-undef */

/**
 * Request Wrapper with default success/error actions
 */

/**
 * @description
 * @param {object} options : API通信用の設定
 * @param {boolean} showLoading : 既存のローディングダイアログを表示するか？
 *                  default = true (ローディングダイアログを表示する)
 */
const request = (options, showLoading = true) => {
  const onSuccess = response => {
    if (_.isEmpty(response) && isProduction()) {
      return window.location.replace('/error-500');
    }
    return response.data;
  };

  let ajaxDone = false;

  if (!('isQuiet' in options)) {
    options.isQuiet = false;
  }

  const onError = error => {
    ajaxDone = true;
    tryHideFullScreenLoading();
    return Promise.reject(error.response || error.message);
  };

  client.interceptors.request.use(function(config) {
    /**
     * token更新時にsessionが切れているとtokenが空、あるいはnullでAPIアクセスするケースがある
     * session切れ + tokenが不正時はAPIアクセスをキャンセルしログアウト処理の完了を待つ
     * BLDE_V2-26358
     * */
    if (config.loginExpired && _.has('config', 'headers.Authorization')) {
      const authorizationArray = config.headers.Authorization.split(' ');
      const tokenString = authorizationArray[1].trim();
      if (tokenString === '' || 'null') {
        axios.Cancel();
      }
    }

    // IE11 HTTPステータス304のキャッシュ問題の修正のため、リクエストのQuery Parameterに
    // タイムスタンプを付加。(重い場合は別の対策を考える)
    // ref. https://s8a.jp/no-cache-using-ajax-with-ie11#axios%E3%81%AE%E5%A0%B4%E5%90%88
    if (_.isUndefined(config.params)) {
      config.params = {};
    }
    if (!_.isObject(config.params)) {
      throw new Error('request.js config.params is not object');
    }
    config.params._ = Date.now();

    // Log出力用にヘッダにリクエストIDを付加する
    if (_.isUndefined(config.headers)) {
      config.headers = {};
    }
    if (!_.isObject(config.headers)) {
      throw new Error('request.js config.headers is not object');
    }
    config.headers['X-API-REQUEST-ID'] = uuid.v4();

    if (showLoading === true) {
      setTimeout(function() {
        if (!ajaxDone && !options.isQuiet) {
          showFullScreenLoading();
        }
      }, 500);
    }
    return config;
  });

  client.interceptors.response.use(function(config) {
    ajaxDone = true;
    tryHideFullScreenLoading();
    return config;
  });

  return client(options)
    .then(onSuccess)
    .catch(onError);
};

const withToken = async axiosOptions => {
  let token = SessionStorageService.getToken();
  let loginExpired = false;
  if (_.isEmpty(token)) {
    console.error('request.js withToken', 'token is empty.');
    await AuthProvider.instance.redirectLoginPage();
  }

  if (needTokenUpdate(token)) {
    console.log('request.js withToken silentTokenUdate');
    token = await AuthProvider.instance.silentTokenUdate();
    if (_.isEmpty(token)) {
      loginExpired = true;
    }
  }

  return _.merge(
    { headers: { Authorization: `Bearer ${token}` }, loginExpired },
    axiosOptions
  );
};

/**
 * @description
 * @param {object} options : API通信用の設定
 * @param {boolean} useDefaultCatch : 既存のエラー処理を実行するか？
 *                  default = true (既存に任せる)
 * @param {boolean} showLoading : 既存のローディングダイアログを表示するか？
 *                  default = true (既存に任せる)
 *  useDefaultCatch について
 *    true を指定した場合は requestWithToken関数内にかかれている
 *    「既存のエラー処理」に処理がいきます。
 *    false を指定した場合は エラー処理は requestWithToken を呼び出した場所で
 *    .catch を記載することで対応できます。
 *    APIごとに細かなエラー制御を行いたい場合(ほとんどの場合はこれです)は
 *    false にして呼び出し側でエラー処理を行ってください。
 *    デフォルトが true になっている理由は、既存の古いAPI呼び出しの多くが
 *    「既存のエラー処理」で実装されていたからです。
 *
 *  validateStatus について
 *  validateStatus は request関数内で使われている axios のプロパティです
 *    参考：https://github.com/axios/axios
 *  何をエラー処理にするか、正常処理にするかを制御することができ
 *  指定しない場合は 200 番台が then で返り、それ以外は catch で返ります。
 */
export const requestWithToken = async (
  options,
  useDefaultCatch = true,
  showLoading = true
) => {
  if (!useDefaultCatch) {
    return request(await withToken(options), showLoading);
  }
  return request(await withToken(options), showLoading).catch(error => {
    // console.log('options', options);
    // 下記の戻り値について
    // エラー処理をした場合は
    // ページ移動(リダイレクト)処理をして
    // return false をするとよいです。
    // エラー処理をしない場合は
    // return error; してください

    const errorMessage = error.toString();
    if (options.action === 'print') {
      const reader = new FileReader();
      reader.readAsText(error.data);
      reader.onload = function() {
        const errorMess = JSON.parse(reader.result);
        if (options.hasFilter === 1) {
          // alert(errorMess.message);
        } else {
          sessionStorage.setItem('print_error', errorMess.message);
          window.location.replace('/error-500');
        }
      };
      return false;
    }
    if (options.action === 'printList') {
      // 一覧画面からの帳票印刷固有のエラーの場合は呼び出し元でエラーハンドリングします。
      // それ以外の場合は他同様本関数にてエラーハンドリングします。
      // We only return error in this time if error.status is one of the error of the printList
      // If not, let them as usual
      if (PrintService.getErrorMessageForList(error.status, null)) {
        error.result = false;
        return error;
      }
    }
    if (errorMessage.includes('Network Error')) {
      console.error(
        'request.js requestWithToken',
        'ネットワークエラーです',
        errorMessage
      );
      return false;
    }

    switch (error.status) {
      // force logout, if authorization failed.
      case 400: {
        return error;
      }
      case 401: {
        // エラーメッセージをダイアログに出力後、ログアウト処理を実行
        dialogLoader.show(
          CcusErrorDialog,
          {
            callback: {
              done: () => {
                AuthProvider.instance.logout();
              },
            },
            message:
              error.data.message || messageUtility.getMessage('E-000-036'),
          },
          null
        );
        return false;
      }
      case 403: {
        return error;
      }
      case 404: {
        // APIからのレスポンスが404でも権限エラーとして扱う
        if (isProduction()) {
          window.location.replace('/error-permission');
        }
        return false;
      }
      case 409: {
        return error;
      }
      case 422: {
        return error;
      }
      case 500: {
        console.error(
          'request.js requestWithToken',
          'ネットワークエラー 500 です',
          JSON.stringify(error)
        );
        window.location.replace('/error-500');
        return false;
      }
      case 503: {
        window.location.replace('/');
        return false;
      }
      default: {
        console.error(
          'request.js requestWithToken',
          'ネットワークエラーです',
          errorMessage
        );

        window.location.replace('/error-500');
        return false;
      }
    }
  });
};

/**
 * @description : request URL with success/error actions
 * @param {object} options : API通信用の設定
 */
export const requestURL = options => {
  const onSuccess = response => {
    if (_.isEmpty(response) && isProduction()) {
      return window.location.replace('/error-500');
    }
    return Promise.resolve(response);
  };

  const onError = error => {
    return Promise.reject(error.response || error.message);
  };

  return axios(options)
    .then(onSuccess)
    .catch(onError);
};

export default request;



// WEBPACK FOOTER //
// ./src/utilities/request.js